import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Badge,
} from '@mui/material';
import {
  Bell,
  ArrowLeft,
  Timer,
  Bug,
} from 'lucide-react';

import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { selectActiveTimers } from '@/store/slices/timerSlice';
import { useUnreadCount } from '@/hooks/useNotifications';
import NotificationsDrawer from './NotificationsDrawer';
import { getPageHeader } from '@/shared/config/pageHeaders';

interface MobileHeaderProps {
  title?: string;
  showBack?: boolean;
  rightAction?: React.ReactNode;
  transparent?: boolean;
  onReportIssue?: () => void;
}

/**
 * MobileHeader
 * Simplified header for mobile devices
 */
const MobileHeader: React.FC<MobileHeaderProps> = ({
  title,
  showBack = false,
  rightAction,
  transparent = false,
  onReportIssue,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAppSelector(selectCurrentUser);
  const activeTimers = useAppSelector(selectActiveTimers);
  const { data: unreadCount } = useUnreadCount();
  const [notificationsPanelOpen, setNotificationsPanelOpen] = useState(false);

  // Get page title based on route
  const getPageTitle = (): string => {
    if (title) return title;
    return getPageHeader(location.pathname).title;
  };

  // Determine if we should show back button
  const shouldShowBack = (): boolean => {
    if (showBack) return true;
    const path = location.pathname;
    // Show back for detail pages
    return (
      path.startsWith('/tasks/') ||
      path.startsWith('/projects/') ||
      path.startsWith('/team/')
    );
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        display: { xs: 'block', md: 'none' },
        bgcolor: transparent ? 'transparent' : 'background.paper',
        borderBottom: transparent ? 'none' : '1px solid',
        borderColor: 'divider',
      }}
    >
      <Toolbar
        sx={{
          minHeight: 56,
          px: 1.5,
          justifyContent: 'space-between',
        }}
      >
        {/* Left Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {shouldShowBack() ? (
            <IconButton
              onClick={handleBack}
              size="small"
              sx={{ color: 'text.primary' }}
            >
              <ArrowLeft size={22} />
            </IconButton>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                }}
              >
                {user?.firstName?.[0]?.toUpperCase() || 'U'}
              </Avatar>
            </Box>
          )}
          
          <Typography
            variant="h6"
            sx={{
              color: 'text.primary',
              fontWeight: 600,
              fontSize: '1.1rem',
            }}
          >
            {getPageTitle()}
          </Typography>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {rightAction || (
            <>
              {/* Active Timers Badge */}
              {activeTimers.length > 0 && (
                <IconButton
                  size="small"
                  onClick={() => navigate('/tasks')}
                  sx={{
                    bgcolor: 'success.50',
                    color: 'success.main',
                    '&:active': { bgcolor: 'success.100' },
                  }}
                >
                  <Badge
                    badgeContent={activeTimers.length}
                    color="success"
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: '0.6rem',
                        minWidth: 14,
                        height: 14,
                        right: -2,
                        top: -2,
                      },
                    }}
                  >
                    <Timer size={18} className="timer-running" />
                  </Badge>
                </IconButton>
              )}

              {/* Report issue */}
              {onReportIssue && (
                <IconButton
                  size="small"
                  sx={{ color: 'text.secondary' }}
                  onClick={onReportIssue}
                >
                  <Bug size={20} />
                </IconButton>
              )}

              {/* Notifications */}
              <IconButton 
                size="small" 
                sx={{ color: 'text.secondary' }}
                onClick={() => setNotificationsPanelOpen(true)}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <Bell size={20} />
                </Badge>
              </IconButton>
            </>
          )}
        </Box>
      </Toolbar>

      {/* Notifications Drawer */}
      <NotificationsDrawer
        open={notificationsPanelOpen}
        onClose={() => setNotificationsPanelOpen(false)}
      />
    </AppBar>
  );
};

export default MobileHeader;
